import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-brown-50">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-lg bg-white/60 border-b border-cream-300/50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-brown-600 hover:text-brown-900 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Back
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border border-cream-300/50 p-6 sm:p-8 md:p-10">
          
          {/* Title */}
          <div className="mb-8 sm:mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-brown-900 mb-3 sm:mb-4">
              Terms of Service
            </h1>
            <p className="text-sm sm:text-base text-brown-600">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Introduction */}
          <div className="prose max-w-none space-y-6 sm:space-y-8 text-brown-800">
            
            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                1. Agreement & Service Description
              </h2>
              <p className="text-sm sm:text-base leading-relaxed">
                Welcome to OmniClarix. By using our platform, you agree to these Terms. OmniClarix provides an AI-powered academic assistant that converts textbook images and learning materials into clear summaries, definitions, and study quizzes tailored for students in Nigeria.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                2. Eligibility & Account Security
              </h2>
              <p className="text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                You must be at least 13 years old to use this platform. By creating an account, you agree to provide accurate information, protect your secure password credentials, and accept complete responsibility for all activities occurring under your profile dashboard.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                3. Subscriptions & Payment Processing
              </h2>
              <p className="text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                We offer a basic Free plan alongside tiered Premium subscription models:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-4 sm:ml-6 mb-4">
                <li><strong>Free Plan:</strong> Includes 3 daily page uploads, basic summaries, and keyword extractions.</li>
                <li><strong>Weekly Premium Access:</strong> ₦1,000 for 7 consecutive days.</li>
                <li><strong>Monthly Premium Access:</strong> ₦3,000 for 30 consecutive days.</li>
                <li><strong>Semester Premium Access:</strong> ₦10,000 for 120 consecutive days.</li>
              </ul>
              <p className="text-sm sm:text-base leading-relaxed">
                All financial transactions are routed through our secure third-party payment partner, <strong>Paystack</strong>. We accept local debit cards (Visa, Mastercard, Verve), standard bank transfers, USSD strings, and mobile money options. Subscriptions do not auto-renew automatically.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                4. Acceptable Use Guidelines
              </h2>
              <p className="text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                You agree to use our platform solely for regular educational purposes. You must not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-4 sm:ml-6">
                <li>Share individual premium access profiles or account logins with others.</li>
                <li>Deploy automated scraping bots, scripts, or bypass tools to trick daily upload limitations.</li>
                <li>Attempt to reverse-engineer, decompile, or steal the core AI model prompts or system structures.</li>
                <li>Upload malicious files, illegal materials, or offensive materials that disrupt the platform.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                5. Intellectual Property & Content Rights
              </h2>
              <p className="text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                <strong>Your Content:</strong> You retain complete ownership rights over your uploaded textbook pages and course documents. You grant us a restricted processing license simply to run the system tools for you.
              </p>
              <p className="text-sm sm:text-base leading-relaxed">
                <strong>Our Platform:</strong> All visual assets, logos, software frameworks, designs, and server code configurations are the exclusive property of OmniClarix and are legally protected.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                6. AI Accuracy Disclaimer
              </h2>
              <p className="text-sm sm:text-base leading-relaxed">
                <strong>Important Notice:</strong> While our advanced AI framework strives for high contextual accuracy, AI systems can occasionally misread details or generate mistakes. Always cross-check critical definitions with your instructors and your original physical textbooks. We do not guarantee specific academic grades or exam outcomes.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                7. Cancellation & 7-Day Refund Window
              </h2>
              <p className="text-sm sm:text-base leading-relaxed">
                You can cancel your subscription plans inside your user settings anytime. We proudly offer an official <strong>7-day money-back guarantee</strong> for first-time premium accounts. If you are unsatisfied, contact <strong>petermikel366@gmail.com</strong> within 7 days of initial registration to receive a processing refund within 5-7 business working days.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                8. Governing Law
              </h2>
              <p className="text-sm sm:text-base leading-relaxed">
                These dynamic Terms are structured and governed in accordance with the regulatory legal guidelines of the Federal Republic of Nigeria.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brown-900 mb-3 sm:mb-4">
                9. Contact Details
              </h2>
              <div className="bg-cream-100/50 rounded-lg p-4 sm:p-6 border border-cream-300/50">
                <p className="text-sm sm:text-base text-brown-800 mb-2">
                  <strong>Support Email:</strong> petermikel366@gmail.com
                </p>
                <p className="text-sm sm:text-base text-brown-800 mb-2">
                  <strong>Web domain:</strong> www.omniclarix.com
                </p>
                <p className="text-sm sm:text-base text-brown-800">
                  <strong>Desk Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM WAT
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <div className="mt-8 sm:mt-10 p-4 sm:p-6 bg-brown-50/50 border-2 border-brown-200/50 rounded-lg sm:rounded-xl">
              <p className="text-sm sm:text-base text-brown-800 font-medium">
                By creating an account or using OmniClarix, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;